/**
 * Utilities catalog — the shared model behind the "Utilities" hub, a top-level surface (sibling to
 * Integrations) listing small on-demand maintenance workloads. Where an Integration is a standing
 * connection/automation, a Utility is a chore you fire when you want it (the first is Gmail Cleanup:
 * trash low-value notification email older than N days). See docs/plans/utilities-gmail-cleanup.md.
 *
 * This module is the single source of truth for the STATIC catalog (name/icon/description/whether a
 * utility is built + runnable) AND the shared config/run types the client, API, and worker all speak.
 * It intentionally reuses the Integrations status vocabulary + relative-time helpers so the hub/detail
 * can share the `.lapo-int-*` presentation.
 */

import {
  type IntegrationStatus,
  type IntegrationAuditLevel,
  relativeTime,
  statusLabel,
} from '../integrations/index.js';

/** A utility's lifecycle state — same vocabulary as an integration's. */
export type UtilityStatus = IntegrationStatus;
/** Broad grouping for the (future) filter menu. Only one family today. */
export type UtilityCategory = 'maintenance';

// Re-export the shared helpers under utility-flavored names so consumers don't reach into integrations.
export { relativeTime as utilityRelativeTime, statusLabel as utilityStatusLabel };

/** The most-recent-run summary shown on a card + atop the detail page. */
export interface UtilityActivity {
  /** Minutes before "now"; resolved by {@link relativeTime}. */
  agoMinutes: number;
  summary: string;
  ok: boolean;
}

/**
 * A single utility — the client-facing card DTO the hub renders. Structurally in the same family as an
 * {@link import('../integrations/index.js').Integration} so it can share the card presentation.
 */
export interface Utility {
  id: string;
  name: string;
  description: string;
  /** Material Symbols icon name (snake_case). */
  icon: string;
  category: UtilityCategory;
  status: UtilityStatus;
  /** Whether the card/detail shows a Run button (built AND declared runnable). */
  runnable: boolean;
  lastAction?: UtilityActivity;
}

/**
 * Static definition of one utility. `requires` lists the linked-account integration keys whose presence
 * marks it connected (Gmail Cleanup needs a `gmail` account). `available` is whether it's built yet — an
 * unbuilt one always reads `coming-soon`.
 */
export interface UtilityDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: UtilityCategory;
  runnable: boolean;
  available: boolean;
  requires: string[];
  /** Seed for the offline/demo catalog. */
  demo: { status: UtilityStatus; lastAction?: UtilityActivity };
}

/** Compute a utility's live status from its definition and whether a required account is connected. */
export function computeUtilityStatus(def: UtilityDefinition, connected: boolean): UtilityStatus {
  if (!def.available) return 'coming-soon';
  if (def.requires.length === 0) return 'active';
  return connected ? 'active' : 'paused';
}

/** Whether a definition can be triggered on demand right now (built AND runnable). */
export function isUtilityRunnable(def: UtilityDefinition): boolean {
  return def.available && def.runnable;
}

/** Filter inputs for the hub: free-text query + optional status scope. */
export interface UtilityFilter {
  query?: string;
  status?: UtilityStatus | 'all';
}

/** Apply the hub's filter — pure, order-preserving. */
export function filterUtilities(list: Utility[], filter: UtilityFilter = {}): Utility[] {
  const q = (filter.query ?? '').trim().toLowerCase();
  const status = filter.status ?? 'all';
  return list.filter((u) => {
    if (status !== 'all' && u.status !== status) return false;
    if (!q) return true;
    return u.name.toLowerCase().includes(q) || u.description.toLowerCase().includes(q);
  });
}

/** Look up a static definition by id. */
export function findUtilityDefinition(id: string): UtilityDefinition | undefined {
  return UTILITY_DEFS.find((d) => d.id === id);
}

/** The utility id for Gmail Cleanup (used in the hub catalog, config docs, and run docs). */
export const GMAIL_CLEANUP_UTILITY_ID = 'gmail-cleanup';

/**
 * The built-in utilities catalog. Gmail Cleanup is the flagship (needs a linked Gmail account); the two
 * coming-soon stubs prove the framework shape (IMPL-11). Add to this list as utilities land — the hub +
 * API pick them up with no other changes.
 */
export const UTILITY_DEFS: UtilityDefinition[] = [
  {
    id: GMAIL_CLEANUP_UTILITY_ID,
    name: 'Gmail Cleanup',
    description:
      'Moves low-value notification email — calendar invite responses and Google file-share notices — to Trash, so they stop cluttering your search. Recoverable for 30 days.',
    icon: 'mop',
    category: 'maintenance',
    runnable: true,
    available: true,
    requires: ['gmail'],
    demo: {
      status: 'paused',
      lastAction: undefined,
    },
  },
  {
    id: 'inbox-tidy',
    name: 'Inbox Tidy',
    description: 'Archives read promotional and social mail past a chosen age, keeping your inbox to what still needs you.',
    icon: 'inbox',
    category: 'maintenance',
    runnable: false,
    available: false,
    requires: ['gmail'],
    demo: { status: 'coming-soon' },
  },
  {
    id: 'calendar-cleanup',
    name: 'Calendar Cleanup',
    description: 'Clears out declined and cancelled events from your calendar so your agenda reflects only what you actually attend.',
    icon: 'event_busy',
    category: 'maintenance',
    runnable: false,
    available: false,
    requires: ['calendar'],
    demo: { status: 'coming-soon' },
  },
];

/** Build the client-facing card DTO from a definition's demo seed (offline/fallback rendering). */
export function fallbackUtility(def: UtilityDefinition): Utility {
  return {
    id: def.id,
    name: def.name,
    description: def.description,
    icon: def.icon,
    category: def.category,
    status: def.demo.status,
    runnable: isUtilityRunnable(def),
    lastAction: def.demo.lastAction,
  };
}

/** The offline/demo catalog — rendered when the client can't reach the API. */
export const UTILITIES: Utility[] = UTILITY_DEFS.map(fallbackUtility);

// ─── Gmail Cleanup: shared categories, config, and run types ──────────────────────────────────────

/** Stable id of a cleanup category — the single source shared by config, server rules, and the UI. */
export type CleanupCategoryId = 'calendarResponses' | 'fileShares';

/** A cleanup category descriptor for the UI (label + one-line blurb). */
export interface CleanupCategory {
  id: CleanupCategoryId;
  label: string;
  description: string;
}

/** The v1 categories (D6). Both default on. */
export const CLEANUP_CATEGORIES: CleanupCategory[] = [
  {
    id: 'calendarResponses',
    label: 'Calendar invite responses',
    description: '“Accepted:/Declined:/Tentatively accepted: <event>” replies from Google Calendar.',
  },
  {
    id: 'fileShares',
    label: 'File-share notifications',
    description: '“<name> shared … with you” notices from Google Drive/Docs.',
  },
];

/** Age default + bounds for the "delete older than X days" control (D16). */
export const CLEANUP_AGE_DEFAULT_DAYS = 30;
export const CLEANUP_AGE_MIN_DAYS = 7;
export const CLEANUP_AGE_MAX_DAYS = 365;

/** Per-user Gmail Cleanup configuration (Firestore `utilitiesConfig/{userId}.gmailCleanup`). */
export interface GmailCleanupConfig {
  /** Only match mail older than this many days (clamped to [7, 365]). */
  olderThanDays: number;
  /** Which categories are active this run (both default true). */
  categories: Record<CleanupCategoryId, boolean>;
  /** Per-account enable, keyed by linked accountId (D7). Absent/false ⇒ that mailbox is skipped. */
  accounts: Record<string, { enabled: boolean }>;
  /** Per-utility trust: once the owner approves one run, later runs auto-execute (D14). */
  previewApproved: boolean;
  /** The rules version the owner last approved under (bookkeeping / future re-consent prompts). */
  rulesVersion: number;
}

/** The whole `utilitiesConfig/{userId}` doc — an extensible map keyed by utility id. */
export interface UtilitiesConfig {
  gmailCleanup: GmailCleanupConfig;
}

/** Defaults for a brand-new user: nothing enabled, untrusted, 30-day threshold, both categories armed. */
export function defaultGmailCleanupConfig(): GmailCleanupConfig {
  return {
    olderThanDays: CLEANUP_AGE_DEFAULT_DAYS,
    categories: { calendarResponses: true, fileShares: true },
    accounts: {},
    previewApproved: false,
    rulesVersion: 1,
  };
}

function clampAge(n: unknown): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : CLEANUP_AGE_DEFAULT_DAYS;
  return Math.min(CLEANUP_AGE_MAX_DAYS, Math.max(CLEANUP_AGE_MIN_DAYS, v));
}

/** Sanitize an untrusted config blob (from Firestore or a PUT body) into a valid {@link GmailCleanupConfig}. */
export function coerceGmailCleanupConfig(raw: unknown): GmailCleanupConfig {
  const d = defaultGmailCleanupConfig();
  if (!raw || typeof raw !== 'object') return d;
  const r = raw as Record<string, unknown>;
  const cats = (r.categories ?? {}) as Record<string, unknown>;
  const accountsRaw = (r.accounts ?? {}) as Record<string, unknown>;
  const accounts: Record<string, { enabled: boolean }> = {};
  for (const [id, v] of Object.entries(accountsRaw)) {
    if (v && typeof v === 'object') accounts[id] = { enabled: (v as { enabled?: unknown }).enabled === true };
  }
  return {
    olderThanDays: clampAge(r.olderThanDays),
    categories: {
      calendarResponses: cats.calendarResponses !== false,
      fileShares: cats.fileShares !== false,
    },
    accounts,
    previewApproved: r.previewApproved === true,
    rulesVersion: typeof r.rulesVersion === 'number' ? r.rulesVersion : d.rulesVersion,
  };
}

/**
 * Phases of a cleanup run. `queued` is the pre-work state after the api enqueues to the background
 * worker (UBW); `awaiting-approval` is the paused state after a preview (D3).
 */
export type UtilityRunPhase =
  | 'queued'
  | 'previewing'
  | 'awaiting-approval'
  | 'executing'
  | 'done'
  | 'failed'
  | 'canceled';

/** A sample matched message shown in the preview (grouped counts + samples — D11). */
export interface CleanupSample {
  category: CleanupCategoryId;
  subject: string;
  from: string;
  /** Epoch-ms of the message. */
  dateMs: number;
}

/** Per-account rollup within a run. */
export interface UtilityRunAccount {
  accountId: string;
  email: string;
  scanned: number;
  matched: number;
  trashed: number;
  byCategory: Record<CleanupCategoryId, { matched: number; trashed: number }>;
  /** Per-account soft failure: 'needs-permission' | a Gmail error message | 'capped'. Others still run. */
  error?: string;
}

/** The last-run status doc (Firestore `utilityRuns/{userId}__{utilityId}`) — overwritten each run (D10). */
export interface UtilityRun {
  runId: string;
  userId: string;
  utilityId: string;
  phase: UtilityRunPhase;
  action: 'preview' | 'execute';
  olderThanDays: number;
  rulesVersion: number;
  startedAt: number;
  updatedAt: number;
  perAccount: UtilityRunAccount[];
  totals: { scanned: number; matched: number; trashed: number };
  /** Preview only: the exact ids to trash on approval + samples for the UI, keyed by accountId (IMPL-7). */
  pendingByAccount?: Record<string, { ids: string[]; samples: CleanupSample[] }>;
  /**
   * Set while `phase === 'queued'`: what the background worker should run when it picks this doc up
   * (UBW). `approveRunId` present ⇒ approve the previewed ids on THIS doc (which keeps `pendingByAccount`);
   * otherwise a fresh preview or recompute-execute. Cleared once the worker starts the run.
   */
  queued?: { action: 'preview' | 'execute'; approveRunId?: string };
  error?: string;
}

/** Fresh per-account rollup (all zeros). */
export function emptyRunAccount(accountId: string, email: string): UtilityRunAccount {
  return {
    accountId,
    email,
    scanned: 0,
    matched: 0,
    trashed: 0,
    byCategory: { calendarResponses: { matched: 0, trashed: 0 }, fileShares: { matched: 0, trashed: 0 } },
  };
}

/** Sum per-account rollups into run totals. */
export function sumRunTotals(perAccount: UtilityRunAccount[]): UtilityRun['totals'] {
  return perAccount.reduce(
    (acc, a) => ({
      scanned: acc.scanned + a.scanned,
      matched: acc.matched + a.matched,
      trashed: acc.trashed + a.trashed,
    }),
    { scanned: 0, matched: 0, trashed: 0 },
  );
}

/** Whether a run is still in flight (drives whether the UI keeps polling `/status`). Includes `queued`
 * — the api has handed it to the background worker but work hasn't started yet (UBW). */
export function isRunActive(phase: UtilityRunPhase): boolean {
  return phase === 'queued' || phase === 'previewing' || phase === 'executing';
}

/**
 * A run doc is refreshed on every step (per rule, per account). If an `active` run hasn't advanced in
 * this long, the request that owned it died mid-scan — treat it as stalled so the UI stops polling and
 * re-enables a retry instead of spinning on "Scanning…" forever.
 */
export const RUN_STALE_MS = 120_000;

/** Whether an in-flight run appears abandoned (its owning request was killed). `now`/`updatedAt` are ms. */
export function isRunStale(run: Pick<UtilityRun, 'phase' | 'updatedAt'>, now: number): boolean {
  return isRunActive(run.phase) && now - run.updatedAt > RUN_STALE_MS;
}

/** Build a card `UtilityActivity` from a finished run (for the hub list + detail header). */
export function activityFromRun(run: UtilityRun | null, now: number): UtilityActivity | undefined {
  if (!run) return undefined;
  const agoMin = Math.max(0, Math.round((now - run.updatedAt) / 60_000));
  if (run.phase === 'failed') return { agoMinutes: agoMin, summary: run.error ?? 'Run failed', ok: false };
  if (run.phase === 'awaiting-approval') {
    return { agoMinutes: agoMin, summary: `Preview ready — ${run.totals.matched} to review`, ok: true };
  }
  if (run.phase === 'done') {
    const n = run.totals.trashed;
    return { agoMinutes: agoMin, summary: `Trashed ${n} ${n === 1 ? 'email' : 'emails'}`, ok: true };
  }
  const summary = run.phase === 'executing' ? 'Running…' : run.phase === 'queued' ? 'Queued…' : 'Preparing…';
  return { agoMinutes: agoMin, summary, ok: true };
}
